import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiKeyAdminStatusMappingsDocument,
  StatusMappingCategory,
  useApiKeyAdminSetStatusMappingsMutation,
  useApiKeyAdminStatusMappingsQuery,
} from "../../../generated/graphql";
import { validateStatusMappingRows } from "../status-mapping-validation";
import { throwOnGraphqlErrors } from "../throw-on-graphql-errors";

const CATEGORY_OPTIONS = Object.values(StatusMappingCategory);

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    p: 3,
  } satisfies SxProps<Theme>,
  categoryCell: { minWidth: 180 } satisfies SxProps<Theme>,
  nameCell: { minWidth: 200 } satisfies SxProps<Theme>,
};

/**
 * A single editable status-mapping row. `rowId` is a stable client-only key so
 * React reconciliation and edits survive changes to `internalName` (which is
 * part of the natural key but mutable while editing).
 */
type EditableRow = {
  rowId: number;
  category: StatusMappingCategory;
  internalName: string;
  externalName: string;
};

type StatusMappingsDialogProps = {
  readonly open: boolean;
  readonly apiKeyUuid: string;
  readonly onClose: () => void;
};

/**
 * Dialog for managing an API key's custom status mappings
 * (`api_user_status_mappings`). Loads the current rows, lets the admin add,
 * edit, and remove rows, and persists the full set via a single "set"
 * mutation that replaces all mappings for the key.
 *
 * Save is blocked while any row has an empty internal or external name, or when
 * two rows share the same `(category, internalName)` pair (which the backend
 * would reject as a unique-constraint violation).
 */
const StatusMappingsDialog = ({
  open,
  apiKeyUuid,
  onClose,
}: StatusMappingsDialogProps) => {
  const nextRowId = useRef(0);
  // Guards row initialization so it happens once per dialog open. Without it the
  // `cache-and-network` query emits twice (cache then network) and the second
  // emission would discard any edits made in between.
  const initializedRef = useRef(false);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data, loading } = useApiKeyAdminStatusMappingsQuery({
    variables: { apiKeyUuid },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!open) {
      // Reset so the next open re-seeds from a fresh fetch.
      initializedRef.current = false;
      return;
    }
    if (data !== undefined && !initializedRef.current) {
      initializedRef.current = true;
      setRows(
        data.apiKeyAdminStatusMappings.map((mapping) => {
          const rowId = nextRowId.current;
          nextRowId.current += 1;
          return {
            rowId,
            category: mapping.category,
            internalName: mapping.internalName,
            externalName: mapping.externalName,
          };
        }),
      );
      setError(null);
    }
  }, [open, data]);

  const [setStatusMappings, { loading: saving }] =
    useApiKeyAdminSetStatusMappingsMutation({
      refetchQueries: [ApiKeyAdminStatusMappingsDocument],
    });

  const { duplicateKeys, hasEmptyField } = useMemo(
    () => validateStatusMappingRows(rows),
    [rows],
  );

  const canSave = !saving && !hasEmptyField && duplicateKeys.size === 0;

  const updateRow = (rowId: number, patch: Partial<EditableRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)),
    );
  };

  const addRow = () => {
    setRows((prev) => {
      const rowId = nextRowId.current;
      nextRowId.current += 1;
      return [
        ...prev,
        {
          rowId,
          category: StatusMappingCategory.OrderStatus,
          internalName: "",
          externalName: "",
        },
      ];
    });
  };

  const removeRow = (rowId: number) => {
    setRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const handleSave = async () => {
    setError(null);
    try {
      const result = await setStatusMappings({
        variables: {
          input: {
            apiKeyUuid,
            mappings: rows.map((row) => ({
              category: row.category,
              internalName: row.internalName.trim(),
              externalName: row.externalName.trim(),
            })),
          },
        },
      });
      throwOnGraphqlErrors(result);
      onClose();
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    }
  };

  return (
    <Dialog fullWidth open={open} maxWidth="md" onClose={onClose}>
      <DialogTitle>Status mappings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          {duplicateKeys.size > 0 && (
            <Alert severity="warning">
              Two rows share the same category and internal name. Make each
              (category, internal name) unique before saving.
            </Alert>
          )}
          {loading && rows.length === 0 ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.categoryCell}>Category</TableCell>
                  <TableCell sx={styles.nameCell}>Internal name</TableCell>
                  <TableCell sx={styles.nameCell}>External name</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No status mappings. Use &quot;Add row&quot; to create
                        one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row.rowId}>
                    <TableCell sx={styles.categoryCell}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.category}
                        onChange={(e) => {
                          updateRow(row.rowId, {
                            category: e.target.value as StatusMappingCategory,
                          });
                        }}
                      >
                        {CATEGORY_OPTIONS.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={styles.nameCell}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. DELIVERED"
                        value={row.internalName}
                        error={row.internalName.trim() === ""}
                        onChange={(e) => {
                          updateRow(row.rowId, {
                            internalName: e.target.value,
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell sx={styles.nameCell}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Delivered"
                        value={row.externalName}
                        error={row.externalName.trim() === ""}
                        onChange={(e) => {
                          updateRow(row.rowId, {
                            externalName: e.target.value,
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        aria-label="Remove row"
                        size="small"
                        onClick={() => {
                          removeRow(row.rowId);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div>
            <Button size="small" variant="outlined" onClick={addRow}>
              Add row
            </Button>
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusMappingsDialog;
